import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import Lightbox from 'react-image-lightbox'
import AliceCarousel from 'react-alice-carousel'
import { Button, Icon, Tooltip } from '@wmgtech/legato'

import 'react-alice-carousel/lib/alice-carousel.css'
import 'react-image-lightbox/style.css'

import { cn } from 'shared/utils'

import { GALLERY_ITEMS_LENGTH } from 'modules/Feed/consts'
import { StoryContext } from 'components/Story/StoryContext'

import { FeedGalleryProps } from './FeedGallery.props'
import './Feedgallery.scss'

export const FeedGallery: FC<FeedGalleryProps> = (props) => {
  const { isEditing = false, removeItem, dataTestPrefix } = props

  const story = useContext(StoryContext)
  const imagePaths = useMemo(
    () => props.imagePaths || story?.gallery || [],
    [props.imagePaths, story?.gallery]
  )

  const imagePathsWithoutPDF = imagePaths.filter((path) => !path?.includes('pdf'))

  const [isLightBoxOpen, toggleLightBox] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)

  const removeItemHandler = useCallback((filePath) => () => removeItem && removeItem(filePath), [removeItem])
  const closeLightbox = useCallback(() => toggleLightBox(false), [])
  const openLightbox = useCallback(
    (idx) => () => {
      setImageIndex(idx)
      toggleLightBox(true)
    },
    []
  )
  const onMovePrevRequest = useCallback(
    () => setImageIndex((imageIndex + imagePathsWithoutPDF.length - 1) % imagePathsWithoutPDF.length),
    [imageIndex, imagePathsWithoutPDF]
  )
  const onMoveNextRequest = useCallback(
    () => setImageIndex((imageIndex + 1) % imagePathsWithoutPDF.length),
    [imageIndex, imagePathsWithoutPDF]
  )

  const carouselPadding = useMemo(() => (imagePaths.length > GALLERY_ITEMS_LENGTH ? 30 : 0), [imagePaths])
  const memoisedItems = useMemo(
    () =>
      imagePaths.map((filePath, idx) => (
        <div key={idx} className={cn('feed-gallery__item-wrap', { 'feed-gallery__item-wrap--edit': isEditing })}>
          <div className="feed-gallery__item">
            {isEditing && (
              <Button
                className="feed-gallery__delete-item"
                colorType="primary"
                containerStyle="solid"
                icon="trash-xmark"
                label=""
                onClick={removeItemHandler(filePath)}
                size="md"
                data-test={`${dataTestPrefix}_clear_btn`}
              />
            )}
            {filePath?.includes('pdf') ? (
              <Tooltip message={filePath.split('NAME')[1] || 'PDF File'} colorType="dark" placement="right">
                <a target="blank" href={filePath} className="feed-gallery__pdf" rel="noreferrer">
                  <Icon name="file-pdf" />
                </a>
              </Tooltip>
            ) : (
              <img
                key={filePath}
                src={filePath}
                alt=""
                onClick={openLightbox(idx)}
                className="feed-gallery__image"
                data-test={`${dataTestPrefix}_image`}
              />
            )}
          </div>
        </div>
      )),
    [imagePaths, isEditing, removeItemHandler, dataTestPrefix, openLightbox]
  )
  const memoisedUploadButtons = useMemo(
    () => [
      <a key={'download'} download={true} href={imagePaths[imageIndex]} className="feed-gallery__upload_button">
        <Icon name="download" />
      </a>
    ],
    [imagePaths, imageIndex]
  )
  if(!imagePaths.length) return null
  return (
    <div className="feed-gallery">
      <AliceCarousel
        disableDotsControls
        mouseTracking
        autoWidth
        disableButtonsControls={memoisedItems.length <= GALLERY_ITEMS_LENGTH}
        paddingLeft={carouselPadding}
        paddingRight={carouselPadding}
        responsive={{ 1024: { items: GALLERY_ITEMS_LENGTH } }}
        renderPrevButton={() => (
          <Button
            className="feed-gallery__angle"
            colorType="primary"
            containerStyle="link"
            icon="angle-left"
            label=""
            size="md"
            data-test={`${dataTestPrefix}_prev_btn`}
          />
        )}
        renderNextButton={() => (
          <Button
            className="feed-gallery__angle"
            colorType="primary"
            containerStyle="link"
            icon="angle-right"
            label=""
            size="md"
            data-test={`${dataTestPrefix}_next`}
          />
        )}
        items={memoisedItems}
      />
      {isLightBoxOpen && (
        <Lightbox
          mainSrc={imagePathsWithoutPDF[imageIndex]}
          nextSrc={imagePathsWithoutPDF[(imageIndex + 1) % imagePathsWithoutPDF.length]}
          prevSrc={imagePathsWithoutPDF[(imageIndex + imagePathsWithoutPDF.length - 1) % imagePathsWithoutPDF.length]}
          onCloseRequest={closeLightbox}
          onMovePrevRequest={onMovePrevRequest}
          onMoveNextRequest={onMoveNextRequest}
          toolbarButtons={memoisedUploadButtons}
        />
      )}
    </div>
  )
}
